import React, { useEffect, useState } from "react";
import { useField } from "formik";
import { FormikFormGroup, InputOptionalInfo } from "components/common";
import { Button, ControlLabel } from "components/bootstrap";
import { RuleBuilderTypes } from "./types";
const SupportedFieldTypes = [
  RuleBuilderTypes.String,
  RuleBuilderTypes.Object,
  RuleBuilderTypes.Number,
  RuleBuilderTypes.Boolean
];
const RuleBlockFormField = ({
  param,
  functionName,
  blockId = void 0,
  order,
  outputVariableList = [],
  blockType,
  resetField
}) => {
  const [primaryInputToggle, setPrimaryInputToggle] = useState(void 0);
  const [field, fieldMeta] = useField(param.name);
  useEffect(() => {
    setPrimaryInputToggle(void 0);
  }, [functionName]);
  const paramValueExists = (paramValue) => typeof paramValue !== "undefined" && paramValue !== null;
  const paramValueIsVariable = (paramValue) => typeof paramValue === "string" && paramValue.startsWith("$");
  const shouldHandlePrimaryParam = () => {
    if (!param.rule_builder_variable) return false;
    if (order === 0) return false;
    return true;
  };
  const validateTextField = (value) => {
    if (paramValueExists(value) && paramValueIsVariable(value)) {
      return "Fields starting with '$' are not allowed.";
    }
    return null;
  };
  const onPrimaryInputToggle = (type) => {
    setPrimaryInputToggle(type);
    resetField(param.name);
  };
  const filteredOutputVariableList = () => outputVariableList.filter((outputVariable) => {
    if (outputVariable.blockId === blockId) return false;
    if (outputVariable.stepOrder >= order) return false;
    if (param.type === RuleBuilderTypes.Object) return true;
    return outputVariable.variableType === param.type;
  });
  const primaryInputButtonAfter = () => {
    if (!shouldHandlePrimaryParam() || filteredOutputVariableList().length <= 0) return null;
    return /* @__PURE__ */ React.createElement(Button, { onClick: () => onPrimaryInputToggle("select") }, "Use output from previous steps");
  };
  const showOutputVariableSelect = () => {
    if (!shouldHandlePrimaryParam()) return false;
    if (filteredOutputVariableList().length <= 0) return false;
    if (!SupportedFieldTypes.includes(param.type)) return false;
    if (primaryInputToggle === "select") return true;
    if (typeof primaryInputToggle !== "undefined") return false;
    return !fieldMeta.initialValue || fieldMeta.initialValue === "" || paramValueIsVariable(fieldMeta.initialValue);
  };
  const labelText = (labelParam) => {
    if (labelParam.optional) {
      return /* @__PURE__ */ React.createElement(React.Fragment, null, labelParam.name, " ", /* @__PURE__ */ React.createElement(InputOptionalInfo, null));
    }
    return labelParam.name;
  };
  if (showOutputVariableSelect()) {
    return /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        type: "select",
        key: `${functionName}_${param.name}`,
        name: param.name,
        label: labelText(param),
        required: !param.optional,
        buttonAfter: /* @__PURE__ */ React.createElement(Button, { onClick: () => onPrimaryInputToggle("custom") }, `Set custom ${param.name}`),
        help: param.description,
        ...field
      },
      /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("option", { key: "placeholder", value: "" }, "Select output from list"), filteredOutputVariableList().map(({ variableName, stepOrder }) => /* @__PURE__ */ React.createElement(
        "option",
        {
          key: `option-${variableName}`,
          value: variableName
        },
        `Output from step ${stepOrder + 1} (${variableName})`
      )))
    );
  }
  const typeNotFoundErrorMessage = `No previous action returns type ${param.type.slice(param.type.lastIndexOf(".") + 1)}`;
  switch (param.type) {
    case RuleBuilderTypes.String:
    case RuleBuilderTypes.Object:
      return /* @__PURE__ */ React.createElement(
        FormikFormGroup,
        {
          type: "text",
          key: `${functionName}_${param.name}`,
          name: param.name,
          label: labelText(param),
          required: !param.optional,
          validate: validateTextField,
          buttonAfter: primaryInputButtonAfter(),
          help: param.description,
          ...field
        }
      );
    case RuleBuilderTypes.Number:
      return /* @__PURE__ */ React.createElement(
        FormikFormGroup,
        {
          type: "number",
          key: `${functionName}_${param.name}`,
          name: param.name,
          label: labelText(param),
          required: !param.optional,
          buttonAfter: primaryInputButtonAfter(),
          help: param.description,
          ...field
        }
      );
    case RuleBuilderTypes.Boolean:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, labelText(param)), /* @__PURE__ */ React.createElement(
        FormikFormGroup,
        {
          type: "checkbox",
          key: `${functionName}_${param.name}`,
          name: param.name,
          label: field.value ? "true" : "false",
          help: param.description,
          checked: field.value,
          buttonAfter: primaryInputButtonAfter(),
          ...field
        }
      ));
    default:
      if (blockType === "action") {
        return /* @__PURE__ */ React.createElement(
          FormikFormGroup,
          {
            type: "select",
            key: `${functionName}_${param.name}`,
            name: param.name,
            label: labelText(param),
            required: !param.optional,
            help: !filteredOutputVariableList().length && param.optional ? typeNotFoundErrorMessage : param.description,
            error: !filteredOutputVariableList().length && !param.optional ? typeNotFoundErrorMessage : void 0,
            ...field
          },
          /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("option", { key: "placeholder", value: "" }, "Select output from list"), filteredOutputVariableList().map(({ variableName, stepOrder }) => /* @__PURE__ */ React.createElement(
            "option",
            {
              key: `option-${variableName}`,
              value: variableName
            },
            `Output from step ${stepOrder + 1} (${variableName})`
          )))
        );
      }
      return null;
  }
};
export default RuleBlockFormField;
